import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ConversationList from './components/ConversationList';
import ChatArea from './components/ChatArea';
import ContactsPanel from './components/ContactsPanel';
import NewConversationModal from './components/NewConversationModal';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Conversation, Message, Contact, TypingIndicator, User } from './types';
import { type SubscriptionFeature } from '../../lib/subscriptionGate';
import { toAbsoluteApiMediaUrl } from '../../lib/mediaUrl';

type BackendContact = {
  id: number;
  full_name: string;
  role: string;
  avatar_url?: string | null;
  classroom_name?: string | null;
  children?: Array<{
    id: number;
    full_name: string;
    classroom_name?: string | null;
  }> | null;
};

type BackendLastMessage = {
  id: number;
  sender_id: number;
  sender_name: string;
  sender_role: string;
  content: string;
  timestamp: string;
};

type BackendConversation = {
  id: string;
  type: 'private' | 'group';
  name: string;
  participants: BackendContact[];
  last_message?: BackendLastMessage | null;
  unread_count?: number;
  is_pinned?: boolean;
  created_at: string;
};

type BackendMessage = {
  id: number;
  conversation_id: string;
  sender_id: number;
  sender_name: string;
  sender_role: string;
  sender_avatar_url?: string | null;
  content: string;
  timestamp: string;
  edited_at?: string | null;
  is_deleted?: boolean;
  attachments?: Array<{
    id: number;
    name: string;
    type: string;
    size: number;
    url: string;
    thumbnail_url?: string | null;
  }> | null;
};

const toAbsoluteApiUrl = (url: string) => {
  const raw = String(url || '');
  if (!raw) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
  if (raw.startsWith('/')) return `${base}${raw}`;
  return `${base}/${raw}`;
};

const roleToChatRole = (role: string): User['role'] => {
  const r = String(role || '').trim().toLowerCase();
  switch (r) {
    case 'admin':
      return 'admin';
    case 'secretariat':
      return 'secretariat';
    case 'profesor':
    case 'teacher':
      return 'teacher';
    case 'elev':
    case 'student':
      return 'student';
    case 'parinte':
    case 'parent':
      return 'parent';
    default:
      return 'student';
  }
};

const mapContact = (c: BackendContact): Contact => ({
  id: String(c.id),
  name: c.full_name,
  role: roleToChatRole(c.role),
  avatar: toAbsoluteApiMediaUrl(c.avatar_url) ?? (c.avatar_url ?? undefined),
  isOnline: false,
  department: undefined,
  grade: c.classroom_name ?? undefined,
  children: (c.children ?? undefined)?.map((child) => ({
    id: String(child.id),
    name: child.full_name,
    classroomName: child.classroom_name ?? undefined,
  })),
});

const mapLastMessage = (m: BackendLastMessage, conversationId: string): Message => ({
  id: String(m.id),
  conversationId,
  senderId: String(m.sender_id),
  senderName: m.sender_name,
  senderRole: roleToChatRole(m.sender_role),
  senderAvatar: undefined,
  content: m.content,
  timestamp: new Date(m.timestamp),
  isRead: true,
});

const mapConversation = (c: BackendConversation): Conversation => ({
  id: c.id,
  type: c.type,
  name: c.name,
  participants: c.participants.map((p) => ({
    id: String(p.id),
    name: p.full_name,
    role: roleToChatRole(p.role),
    avatar: toAbsoluteApiMediaUrl(p.avatar_url) ?? (p.avatar_url ?? undefined),
    isOnline: false,
    children: (p.children ?? undefined)?.map((child) => ({
      id: String(child.id),
      name: child.full_name,
      classroomName: child.classroom_name ?? undefined,
    })),
  })),
  lastMessage: c.last_message ? mapLastMessage(c.last_message, c.id) : undefined,
  unreadCount: c.unread_count ?? 0,
  isPinned: c.is_pinned ?? false,
  createdAt: new Date(c.created_at),
});

const mapMessage = (m: BackendMessage): Message => ({
  id: String(m.id),
  conversationId: m.conversation_id,
  senderId: String(m.sender_id),
  senderName: m.sender_name,
  senderRole: roleToChatRole(m.sender_role),
  senderAvatar: toAbsoluteApiMediaUrl(m.sender_avatar_url) ?? (m.sender_avatar_url ?? undefined),
  content: m.content,
  timestamp: new Date(m.timestamp),
  editedAt: m.edited_at ? new Date(m.edited_at) : undefined,
  isDeleted: m.is_deleted ?? false,
  isRead: true,
  attachments: Array.isArray(m.attachments)
    ? m.attachments.map((a) => ({
        id: String(a.id),
        name: a.name,
        type: a.type,
        size: Number(a.size ?? 0),
        url: toAbsoluteApiUrl(a.url),
        thumbnailUrl: a.thumbnail_url ? toAbsoluteApiUrl(a.thumbnail_url) : undefined,
      }))
    : undefined,
});

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
    return detail || error.message || 'Eroare de rețea';
  }
  return error instanceof Error ? error.message : 'Eroare necunoscută';
};

const isSubscriptionError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  if (Number(error.response?.status) !== 403) return false;
  const detail = String((error.response?.data as any)?.detail || '').toLowerCase();
  return detail.includes('abonament') || detail.includes('subscription');
};

const CommunicationCenter = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { me, isLoading: authLoading } = useAuth();

  const currentUser: User | null = useMemo(() => {
    if (!me) return null;
    return {
      id: String(me.id),
      name: me.full_name,
      role: roleToChatRole(me.role),
      avatar: toAbsoluteApiMediaUrl(me.avatar_url) ?? (me.avatar_url ?? undefined),
    };
  }, [me]);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, Message[]>>({});

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [typingIndicators, setTypingIndicators] = useState<TypingIndicator[]>([]);
  const [showContactsPanel, setShowContactsPanel] = useState(false);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<SubscriptionFeature>('messages');
  const [requestingParentAccess, setRequestingParentAccess] = useState(false);
  const isUnder16Student = me?.role === 'elev' && !Boolean(me?.over16);

  const openUpgrade = useCallback((feature: SubscriptionFeature) => {
    setUpgradeFeature(feature);
    setUpgradeOpen(true);
  }, []);

  const upgradeCopy = useMemo(() => {
    if (upgradeFeature === 'messages') {
      return {
        title: 'Trimite mesaje',
        body: 'Pentru a trimite mesaje ai nevoie de abonamentul Basic (3€) sau Full (5€).',
      };
    }
    if (upgradeFeature === 'comments') {
      return {
        title: 'Comentarii',
        body: 'Pentru a comenta ai nevoie de abonamentul Basic (3€) sau Full (5€).',
      };
    }
    if (upgradeFeature === 'downloads') {
      return {
        title: 'Descărcare resurse',
        body: 'Pentru a descărca resurse ai nevoie de abonamentul Full (5€).',
      };
    }
    if (upgradeFeature === 'calendar') {
      return {
        title: 'Calendar',
        body: 'Pentru a crea programări în calendar ai nevoie de abonamentul Basic (3€) sau Full (5€).',
      };
    }
    return {
      title: 'Funcționalitate premium',
      body: 'Această funcționalitate necesită un abonament.',
    };
  }, [upgradeFeature]);

  const deeplinkDmUserId = searchParams.get('dm');
  const deeplinkName = searchParams.get('name');
  const deeplinkRole = searchParams.get('role');

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refreshContacts = useCallback(async () => {
    const resp = await api.get<BackendContact[]>('/api/users/me/contacts');
    setContacts(resp.data.map(mapContact));
  }, []);

  const refreshConversations = useCallback(async () => {
    const resp = await api.get<BackendConversation[]>('/api/users/me/conversations');
    const serverConversations = resp.data
      .map(mapConversation)
      .map((conv) => {
        if (conv.type !== 'private') return conv;
        const other =
          conv.participants.find((p) => p.id !== String(currentUser?.id || '')) ?? conv.participants[0] ?? null;
        if (!other?.avatar) return conv;
        return { ...conv, avatar: other.avatar };
      });

    // Keep local DM placeholders created from the contacts list until the first
    // real message exists on the server. Otherwise polling will overwrite the
    // local conversation and the UI appears "stuck" on the empty state.
    setConversations((prev) => {
      const serverById = new Set(serverConversations.map((c) => c.id));
      const localPlaceholders = prev.filter(
        (c) => c.id.startsWith('dm-') && !serverById.has(c.id) && !c.lastMessage
      );
      const merged = [...localPlaceholders, ...serverConversations];

      setSelectedConversationId((selectedPrev) => {
        if (selectedPrev && merged.some((c) => c.id === selectedPrev)) return selectedPrev;
        // On mobile, allow "no selection" so the conversations list can remain visible.
        if (!selectedPrev && isMobileView) return null;
        return merged[0]?.id ?? null;
      });

      return merged;
    });
  }, [currentUser?.id, isMobileView]);

  useEffect(() => {
    if (!me) return;
    setIsPageLoading(true);
    Promise.allSettled([refreshContacts(), refreshConversations()]).finally(() => setIsPageLoading(false));
  }, [me, refreshContacts, refreshConversations]);

  useEffect(() => {
    if (!me) return;
    // When entering the communication center, mark everything as read (UX expectation)
    api.post('/api/users/me/mark-all-read', {}).then(() => void refreshConversations()).catch(() => {});
  }, [me, refreshConversations]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const conversationMessages = useMemo(() => {
    if (!selectedConversationId) return [];
    return messagesByConversation[selectedConversationId] ?? [];
  }, [messagesByConversation, selectedConversationId]);

  const loadMessagesIfNeeded = useCallback(
    async (conversationId: string) => {
      if (messagesByConversation[conversationId]) return;
      const resp = await api.get<BackendMessage[]>(`/api/users/conversations/${conversationId}/messages`);
      setMessagesByConversation((prev) => ({ ...prev, [conversationId]: resp.data.map(mapMessage) }));
    },
    [messagesByConversation]
  );

  const refreshMessages = useCallback(async (conversationId: string) => {
    const resp = await api.get<BackendMessage[]>(`/api/users/conversations/${conversationId}/messages`);
    const mapped = resp.data.map(mapMessage);

    setMessagesByConversation((prev) => ({ ...prev, [conversationId]: mapped }));

    // Keep the conversation list in sync for edits/deletes that don't change the message id.
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== conversationId) return c;
        if (!mapped.length) return c;
        const last = mapped[mapped.length - 1];
        if (!last) return c;
        if (!c.lastMessage) return { ...c, lastMessage: last };
        if (String(c.lastMessage.id) !== String(last.id)) return { ...c, lastMessage: last };
        if (c.lastMessage.content !== last.content) return { ...c, lastMessage: last };
        if (String(c.lastMessage.editedAt || '') !== String(last.editedAt || '')) return { ...c, lastMessage: last };
        if (Boolean(c.lastMessage.isDeleted) !== Boolean(last.isDeleted)) return { ...c, lastMessage: last };
        return c;
      })
    );
  }, []);

  // Keep selected chat up-to-date when conversations list updates (new last message / unread changes).
  useEffect(() => {
    if (!selectedConversationId) return;
    const conv = conversations.find((c) => c.id === selectedConversationId);
    const serverLastId = conv?.lastMessage?.id ? String(conv.lastMessage.id) : null;
    if (!serverLastId) return;
    const local = messagesByConversation[selectedConversationId];
    const localLastId = local?.length ? String(local[local.length - 1]?.id) : null;
    if (localLastId && localLastId === serverLastId) return;
    void refreshMessages(selectedConversationId);
  }, [conversations, messagesByConversation, refreshMessages, selectedConversationId]);

  // Poll for new messages + refresh on app focus/resume (Capacitor webview doesn't update otherwise).
  useEffect(() => {
    if (!me) return;

    let disposed = false;
    const tick = async () => {
      try {
        await refreshConversations();
      } catch {
        // Best-effort polling; ignore transient network issues.
      }
    };

    const refreshOnVisible = () => {
      if (disposed) return;
      if (document.visibilityState !== 'visible') return;
      void tick();
      if (selectedConversationId) void refreshMessages(selectedConversationId);
    };

    // Initial tick so the view is populated even if the component was cached.
    void tick();

    const t = window.setInterval(() => void tick(), 10_000);
    window.addEventListener('focus', refreshOnVisible);
    document.addEventListener('visibilitychange', refreshOnVisible);

    return () => {
      disposed = true;
      window.clearInterval(t);
      window.removeEventListener('focus', refreshOnVisible);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [me, refreshConversations, refreshMessages, selectedConversationId]);

  // Near-real-time refresh for the currently open conversation (edits/deletes don't change lastMessage id).
  useEffect(() => {
    if (!me) return;
    if (!selectedConversationId) return;

    let disposed = false;
    const tick = async () => {
      if (disposed) return;
      if (document.visibilityState !== 'visible') return;
      try {
        await refreshMessages(selectedConversationId);
      } catch {
        // Best-effort polling; ignore transient network issues.
      }
    };

    void tick();
    const t = window.setInterval(() => void tick(), 2500);
    return () => {
      disposed = true;
      window.clearInterval(t);
    };
  }, [me, refreshMessages, selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId) return;
    void loadMessagesIfNeeded(selectedConversationId);
  }, [loadMessagesIfNeeded, selectedConversationId]);

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setTypingIndicators((prev) => prev.filter((t) => t.conversationId !== conversationId));
    void loadMessagesIfNeeded(conversationId);
    setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)));
    api.post(`/api/users/conversations/${conversationId}/mark-read`, {}).catch(() => {});
  };

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      await api.post(`/api/users/conversations/${conversationId}/hide`, {});

      setMessagesByConversation((prev) => {
        if (!prev[conversationId]) return prev;
        const copy = { ...prev };
        delete copy[conversationId];
        return copy;
      });

      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      setSelectedConversationId((prev) => (prev === conversationId ? null : prev));

      await refreshConversations();
    },
    [refreshConversations]
  );

  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!selectedConversationId || !currentUser) return false;
    const trimmed = content.trim();
    const hasFiles = Boolean(attachments && attachments.length > 0);
    if (!trimmed && !hasFiles) return false;

    try {
      const resp = hasFiles
        ? await api.post<BackendMessage>(
            `/api/users/conversations/${selectedConversationId}/messages-with-files`,
            (() => {
              const form = new FormData();
              form.append('content', trimmed);
              for (const f of attachments || []) form.append('files', f, f.name);
              return form;
            })(),
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
        : await api.post<BackendMessage>(
            `/api/users/conversations/${selectedConversationId}/messages`,
            { content: trimmed }
          );
      const sent = mapMessage(resp.data);

      setMessagesByConversation((prev) => {
        const existing = prev[selectedConversationId] ?? [];
        return { ...prev, [selectedConversationId]: [...existing, sent] };
      });

      setConversations((prev) =>
        prev.map((c) => (c.id === selectedConversationId ? { ...c, lastMessage: sent } : c))
      );
      return true;
    } catch (error) {
      if (isSubscriptionError(error)) {
        openUpgrade('messages');
        return false;
      }
      window.alert(`Nu am putut trimite mesajul: ${getApiErrorMessage(error)}`);
      return false;
    }
  };

  const handleEditMessage = useCallback(
    async (messageId: string, content: string) => {
      if (!selectedConversationId) return;
      const trimmed = content.trim();
      if (!trimmed) return;

      const resp = await api.put<BackendMessage>(
        `/api/users/conversations/${selectedConversationId}/messages/${messageId}`,
        { content: trimmed }
      );
      const updated = mapMessage(resp.data);

      setMessagesByConversation((prev) => {
        const existing = prev[selectedConversationId] ?? [];
        return {
          ...prev,
          [selectedConversationId]: existing.map((m) => (m.id === messageId ? { ...m, ...updated } : m)),
        };
      });

      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== selectedConversationId) return c;
          if (!c.lastMessage) return c;
          if (String(c.lastMessage.id) !== messageId) return c;
          return { ...c, lastMessage: updated };
        })
      );

      await refreshConversations();
    },
    [refreshConversations, selectedConversationId]
  );

  const handleDeleteMessageForMe = useCallback(
    async (messageId: string) => {
      if (!selectedConversationId) return;

      const conversationId = selectedConversationId;

      // Optimistic UI: remove message immediately; revert by reloading on failure.
      setMessagesByConversation((prev) => {
        const existing = prev[conversationId] ?? [];
        return {
          ...prev,
          [conversationId]: existing.filter((m) => m.id !== messageId),
        };
      });

      try {
        await api.post(`/api/users/conversations/${conversationId}/messages/${messageId}/delete-for-me`, {});
        await refreshConversations();
      } catch (error) {
        await refreshMessages(conversationId);
        window.alert(`Nu am putut șterge mesajul: ${getApiErrorMessage(error)}`);
      }
    },
    [refreshConversations, refreshMessages, selectedConversationId]
  );

  const handleDeleteMessageForAll = useCallback(
    async (messageId: string) => {
      if (!selectedConversationId) return;

      const conversationId = selectedConversationId;

      // Optimistic UI: mark as deleted immediately; revert by reloading on failure.
      setMessagesByConversation((prev) => {
        const existing = prev[conversationId] ?? [];
        return {
          ...prev,
          [conversationId]: existing.map((m) =>
            m.id === messageId ? { ...m, isDeleted: true, content: 'Mesaj șters' } : m
          ),
        };
      });

      try {
        const resp = await api.post<BackendMessage>(
          `/api/users/conversations/${conversationId}/messages/${messageId}/delete-for-all`,
          {}
        );
        const updated = mapMessage(resp.data);

        setMessagesByConversation((prev) => {
          const existing = prev[conversationId] ?? [];
          return {
            ...prev,
            [conversationId]: existing.map((m) => (m.id === messageId ? { ...m, ...updated } : m)),
          };
        });

        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== conversationId) return c;
            if (!c.lastMessage) return c;
            if (String(c.lastMessage.id) !== messageId) return c;
            return { ...c, lastMessage: updated };
          })
        );

        await refreshConversations();
      } catch (error) {
        await refreshMessages(conversationId);
        window.alert(`Nu am putut șterge mesajul: ${getApiErrorMessage(error)}`);
      }
    },
    [refreshConversations, refreshMessages, selectedConversationId]
  );

  const handleReactToMessage = (messageId: string, emoji: string) => {
    if (!selectedConversationId || !currentUser) return;
    setMessagesByConversation((prev) => {
      const existing = prev[selectedConversationId] ?? [];
      return {
        ...prev,
        [selectedConversationId]: existing.map((msg) => {
          if (msg.id !== messageId) return msg;
          const existingReaction = msg.reactions?.find((r) => r.userId === currentUser.id);
          if (existingReaction) {
            return { ...msg, reactions: msg.reactions?.filter((r) => r.userId !== currentUser.id) };
          }
          return {
            ...msg,
            reactions: [...(msg.reactions || []), { emoji, userId: currentUser.id, userName: currentUser.name }],
          };
        }),
      };
    });
  };

  const handlePinMessage = (messageId: string) => {
    if (!selectedConversationId) return;
    setMessagesByConversation((prev) => {
      const existing = prev[selectedConversationId] ?? [];
      return {
        ...prev,
        [selectedConversationId]: existing.map((msg) =>
          msg.id === messageId ? { ...msg, isPinned: !msg.isPinned } : msg
        ),
      };
    });
  };

  const ensureDmConversation = useCallback(
    (
      contactId: string,
      fallback?: {
        name?: string;
        role?: User['role'];
      }
    ) => {
      const conversationId = `dm-${contactId}`;
      const contact = contacts.find((c) => c.id === contactId);

      const name = contact?.name || fallback?.name || 'Utilizator';
      const role = contact?.role || fallback?.role || 'teacher';

      setConversations((prev) => {
        const existingIdx = prev.findIndex((c) => c.id === conversationId);
        if (existingIdx >= 0) {
          const existing = prev[existingIdx];
          const participant = existing.participants[0];
          const updated: Conversation = {
            ...existing,
            name,
            participants: [
              {
                ...participant,
                id: contactId,
                name,
                role,
                avatar: contact?.avatar ?? participant?.avatar,
                isOnline: contact?.isOnline ?? participant?.isOnline ?? false,
              },
            ],
          };
          const copy = prev.slice();
          copy[existingIdx] = updated;
          return copy;
        }

        const newConversation: Conversation = {
          id: conversationId,
          type: 'private',
          name,
          participants: [
            {
              id: contactId,
              name,
              role,
              avatar: contact?.avatar,
              isOnline: contact?.isOnline ?? false,
            },
          ],
          unreadCount: 0,
          isPinned: false,
          createdAt: new Date(),
        };
        return [newConversation, ...prev];
      });

      return conversationId;
    },
    [contacts]
  );

  const ensureDmConversationFromParams = useCallback(() => {
    if (!deeplinkDmUserId) return null;
    const contactId = String(deeplinkDmUserId);

    const name = (deeplinkName || '').toString().trim() || 'Utilizator';
    const role =
      deeplinkRole === 'admin' ||
      deeplinkRole === 'teacher' ||
      deeplinkRole === 'student' ||
      deeplinkRole === 'parent'
        ? (deeplinkRole as User['role'])
        : 'teacher';

    return ensureDmConversation(contactId, { name, role });
  }, [deeplinkDmUserId, deeplinkName, deeplinkRole, ensureDmConversation]);

  useEffect(() => {
    if (!deeplinkDmUserId) return;
    const convId = `dm-${deeplinkDmUserId}`;
    setSelectedConversationId(convId);
    void loadMessagesIfNeeded(convId);
    ensureDmConversationFromParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deeplinkDmUserId]);

  useEffect(() => {
    if (!deeplinkDmUserId) return;
    ensureDmConversationFromParams();
  }, [contacts, deeplinkDmUserId, ensureDmConversationFromParams]);

  const handleStartConversation = (contactId: string) => {
    const convId = ensureDmConversation(contactId);
    setSelectedConversationId(convId);
    setShowContactsPanel(false);
  };

  const handleCreateConversation = async (participantIds: string[], conversationName?: string) => {
    if (!participantIds.length) return;
    if (participantIds.length === 1) {
      handleStartConversation(participantIds[0]);
      return;
    }

    const payload = {
      name: (conversationName || 'Grup nou').trim(),
      member_ids: participantIds.map((id) => Number(id)).filter((n) => Number.isFinite(n)),
    };

    const resp = await api.post<{ group_id: number }>('/api/chat/groups', payload);
    await refreshConversations();
    setSelectedConversationId(`group-${resp.data.group_id}`);
  };

  const notificationMessages = useMemo(
    () =>
      conversations
        .filter((conv) => conv.unreadCount > 0)
        .map((conv) => ({
          id: conv.lastMessage?.id || conv.id,
          sender: conv.name,
          senderRole: conv.participants[0]?.role || 'user',
          subject: conv.type === 'group' ? 'Mesaj grup' : 'Mesaj direct',
          preview: conv.lastMessage?.content || 'Mesaj nou',
          timestamp: (conv.lastMessage?.timestamp ?? new Date()).toISOString(),
          isRead: false,
          priority: 'normal' as const,
        })),
    [conversations]
  );

  if (authLoading || isPageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-16 h-screen flex items-center justify-center">
          <div className="text-sm text-muted-foreground">Se încarcă...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-16 h-screen flex items-center justify-center">
          <div className="text-sm text-muted-foreground">Autentifică-te pentru a vedea mesajele.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={currentUser}
        notificationCount={notificationMessages.length}
        onNavigate={(path) => navigate(path)}
      />

      <div className="pt-16 h-[100dvh] flex flex-col">
        <div className="flex-1 min-h-0 flex overflow-hidden">
          <div className={`${isMobileView && selectedConversationId ? 'hidden' : 'w-full lg:w-80'}`}>
            <ConversationList
              conversations={conversations}
              selectedConversationId={selectedConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={() => setShowNewConversationModal(true)}
              onDeleteConversation={(conversationId) => void handleDeleteConversation(conversationId)}
            />
          </div>

	          <div className={`flex-1 min-h-0 min-w-0 ${isMobileView && !selectedConversationId ? 'hidden' : ''}`}>
	            <ChatArea
	              conversation={selectedConversation}
	              messages={conversationMessages}
	              currentUser={currentUser}
              typingIndicators={typingIndicators}
              onSendMessage={handleSendMessage}
              onEditMessage={(messageId, content) => void handleEditMessage(messageId, content)}
              onDeleteMessageForMe={handleDeleteMessageForMe}
              onDeleteMessageForAll={handleDeleteMessageForAll}
              onReactToMessage={handleReactToMessage}
              onPinMessage={handlePinMessage}
              isMobileView={isMobileView}
              onBackToConversations={() => setSelectedConversationId(null)}
              onOpenContacts={() => setShowContactsPanel(true)}
              onDeleteConversation={(conversationId) => void handleDeleteConversation(conversationId)}
            />
          </div>

          {!isMobileView && (
            <div className="w-80">
              <ContactsPanel
                contacts={contacts}
                onStartConversation={handleStartConversation}
                onClose={() => setShowContactsPanel(false)}
              />
            </div>
          )}
        </div>

        {isMobileView && !selectedConversationId && (
          <div className="fixed bottom-4 right-4 z-50">
            <button
              onClick={() => setShowContactsPanel(true)}
              className="flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-colors duration-150"
              aria-label="Vezi contactele"
            >
              <Icon name="Users" size={24} />
            </button>
          </div>
        )}

        {isMobileView && showContactsPanel && (
          <div className="fixed inset-0 z-[1000] bg-background">
            <ContactsPanel
              contacts={contacts}
              onStartConversation={handleStartConversation}
              onClose={() => setShowContactsPanel(false)}
            />
          </div>
        )}
      </div>

      {showNewConversationModal && (
        <NewConversationModal
          contacts={contacts}
          onClose={() => setShowNewConversationModal(false)}
          onCreateConversation={handleCreateConversation}
        />
      )}

      {upgradeOpen ? (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4"
          onMouseDown={() => setUpgradeOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-card border border-border rounded-xl p-5"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="text-lg font-semibold text-foreground">{upgradeCopy.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{upgradeCopy.body}</div>
              </div>
              <button
                className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
                onClick={() => setUpgradeOpen(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setUpgradeOpen(false)}>
                Închide
              </Button>
              <Button
                variant="default"
                iconName={isUnder16Student ? 'Bell' : 'CreditCard'}
                loading={requestingParentAccess}
                onClick={async () => {
                  if (isUnder16Student) {
                    if (requestingParentAccess) return;
                    setRequestingParentAccess(true);
                    try {
                      const resp = await api.post('/api/users/me/subscription-access-request', {
                        feature: upgradeFeature,
                        context: upgradeCopy.title,
                        link: '/subscriptions',
                      });
                      setUpgradeOpen(false);
                      const sent = Number((resp.data as any)?.sent ?? 0);
                      window.alert(
                        sent > 0
                          ? 'Am trimis o notificare catre parinti. Ei pot activa abonamentul pentru a debloca aceasta functionalitate.'
                          : 'Cererea a fost deja trimisa recent catre parinti.'
                      );
                    } catch (e: any) {
                      const msg =
                        e?.response?.data?.detail || e?.message || 'Nu am putut trimite cererea catre parinti.';
                      window.alert(msg);
                    } finally {
                      setRequestingParentAccess(false);
                    }
                    return;
                  }

                  setUpgradeOpen(false);
                  navigate('/subscriptions');
                }}
              >
                {isUnder16Student ? 'Solicita acces parintilor' : 'Vezi abonamente'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CommunicationCenter;
